import { IconAlertTriangle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Banner } from "../../../../components/common/banner";
import { Shimmer } from "../../../../components/common/shimmer";
import { useAuthenticatedBlob } from "../../../../hooks/useAuthenticatedBlob";
import type { SaleDetailsDto, DropshippingAttachmentDto } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});
function formatDateTime(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const DATE_SHORT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_SHORT.format(d);
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="bodySm" tone="muted">{label}</Text>
      <Text variant="bodyBold" tone="dark">{value}</Text>
    </div>
  );
}

interface DeliveryProofCardProps {
  order: SaleDetailsDto;
  onApprove: () => void;
  onReject: () => void;
  isActing?: boolean;
}

export function DeliveryProofCard({ order, onApprove, onReject, isActing }: DeliveryProofCardProps) {
  const drop = order.dropshipping?.[0];
  const attachments: DropshippingAttachmentDto[] =
    order.dropshipping?.flatMap((d) => (d as unknown as { attachments?: DropshippingAttachmentDto[] }).attachments ?? []) ?? [];
  const sellerShipped = drop?.state === "done";
  const buyerReceived = order.buyerReceived === true;
  const proofUploaded = drop?.isSigned === true;
  const deliveryDate = formatDate(drop?.scheduledDate ?? order.validityDate);
  const shippingAddress = order.shippingPartner
    ? [order.shippingPartner.street, order.shippingPartner.city].filter(Boolean).join(", ")
    : null;
  const address = shippingAddress || [order.partner?.street, order.partner?.city].filter(Boolean).join(", ") || "—";

  if (!sellerShipped) return null;

  if (buyerReceived) {
    return (
      <div className="flex flex-col gap-5 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:px-4">
        <div className="flex items-center gap-3">
          <Heading variant="h3" tone="dark">Delivery</Heading>
          <Chip variant="approved">Received by buyer</Chip>
        </div>
        <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 border-t border-borderLight pt-4">
          <InfoCell label="Delivery Address" value={address} />
          <InfoCell label="Delivered By" value={drop?.partner?.name ?? "—"} />
          <InfoCell label="Tracking #" value={drop?.origin ?? "—"} />
          <InfoCell label="Delivery Date" value={deliveryDate} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:px-4">
      <div className="flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-2">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <Heading variant="h3" tone="dark">Delivery Proof</Heading>
          {proofUploaded
            ? <Chip variant="inReview">Proof Uploaded</Chip>
            : <Chip variant="tertiary">Awaiting Proof</Chip>}
        </div>
        <Text variant="bodySm" tone="muted">Uploaded {formatDateTime(drop?.scheduledDate)}</Text>
      </div>

      {proofUploaded && attachments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {attachments.map((att) =>
            att.mimetype.startsWith("image/") ? (
              <ProofImage key={att.id} attachment={att} />
            ) : (
              <ProofFile key={att.id} attachment={att} />
            ),
          )}
        </div>
      )}

      {proofUploaded && attachments.length === 0 && (
        <Text variant="bodySm" tone="muted">Proof marked as uploaded but no files attached.</Text>
      )}

      <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 border-t border-borderLight pt-4">
        <InfoCell label="Delivery Address" value={address} />
        <InfoCell label="Delivered By" value={drop?.partner?.name ?? "—"} />
        <InfoCell label="Tracking #" value={drop?.origin ?? "—"} />
        <InfoCell label="Delivery Date" value={deliveryDate} />
      </div>

      <Banner tone="warning" icon={<IconAlertTriangle size={16} />}>
        Marking as received will trigger the supplier payout for QAR {order.amountTotal.toLocaleString()}. This action cannot be undone.
      </Banner>

      <div className="flex gap-3 max-md:flex-col">
        <Button variant="filledDark" size="md" disabled={isActing} onClick={onApprove}>
          Mark as received (on behalf of buyer)
        </Button>
        <Button variant="secondary" size="md" disabled={isActing} onClick={onReject}>Reject</Button>
      </div>
    </div>
  );
}

function ProofImage({ attachment }: { attachment: DropshippingAttachmentDto }) {
  const { src, error } = useAuthenticatedBlob(attachment.url);
  if (error) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl bg-bgMuted">
        <Text variant="bodyXs" tone="muted">Couldn't load {attachment.name}</Text>
      </div>
    );
  }
  if (!src) return <Shimmer className="h-32 w-full rounded-xl" />;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block h-32 overflow-hidden rounded-xl bg-bgMuted" title={attachment.name}>
      <img src={src} alt={attachment.name} className="h-full w-full object-cover" />
    </a>
  );
}

function ProofFile({ attachment }: { attachment: DropshippingAttachmentDto }) {
  const { src, error } = useAuthenticatedBlob(attachment.url);
  return (
    <div className="flex h-32 flex-col items-center justify-center gap-1 rounded-xl bg-bgMuted px-3 text-center">
      <Text variant="bodySmBold" tone="dark" className="line-clamp-2">{attachment.name}</Text>
      {error ? (
        <Text variant="bodyXs" tone="muted">Couldn't load file</Text>
      ) : src ? (
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-bodyXs underline text-textBody">Open</a>
      ) : (
        <Text variant="bodyXs" tone="muted">Loading…</Text>
      )}
    </div>
  );
}
